import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, Mail } from 'lucide-react';

const Footer = ({ showTestimonials = false }) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
  ];

  const quickLinks = [
    { name: 'Home', id: 'home', scroll: true },
    { name: 'Services', id: 'services', scroll: true },
    { name: 'About Us', href: '/about', scroll: false },
    ...(showTestimonials ? [{ name: 'Testimonials', id: 'testimonials', scroll: true }] : []),
  ];

  const services = [
    { name: 'Financial Intelligence', href: '/finance-intelligence' },
    { name: 'Operational Intelligence', href: '/operational-intelligence' },
    { name: 'Bespoke Orchestration', href: '/bespoke-orchestration' },
    { name: 'AI Toolset Development', href: '/ai-toolset-development' },
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold mb-4">Granite State Cloud Solutions</h3>
            <p className="text-gray-300 mb-6 leading-relaxed">
              Empowering small and midsize businesses with enterprise-grade AI solutions. Your digital transformation is our mission.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center text-gray-300">
                <Mail className="h-5 w-5 mr-3 text-primary-400" />
                <span>contact@gscsnh.com</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={link.id || link.href || index}>
                  <button
                    onClick={() => {
                      if (link.scroll) {
                        scrollToSection(link.id);
                      } else {
                        navigate(link.href);
                        window.scrollTo(0, 0);
                      }
                    }}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                >
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <button
                    onClick={() => {
                      navigate(service.href);
                      window.scrollTo(0, 0);
                    }}
                    className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                  >
                    {service.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Social */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Stay Connected</h4>
            <p className="text-gray-300 mb-4">
              Subscribe to our newsletter for updates and insights.
            </p>
            
            {/* Newsletter Signup */}
            <div className="mb-8">
              <div className="flex">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-white"
                />
                <button className="bg-primary-600 hover:bg-primary-700 px-6 py-2 rounded-r-lg transition-colors duration-200">
                  Subscribe
                </button>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <p className="text-gray-300 mb-4">Follow us on social media:</p>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="bg-gray-800 hover:bg-primary-600 p-3 rounded-lg transition-colors duration-200 group"
                    >
                      <IconComponent className="h-5 w-5 text-gray-300 group-hover:text-white" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Sovereign Guarantee */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="text-center mb-8">
            <p className="text-primary-400 font-semibold text-lg mb-2">
              The Sovereign Guarantee
            </p>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Your Hardware. Your AI Model. Your Control. We build the pipes; you hold the keys.
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {currentYear} Granite State Cloud Solutions. All rights reserved.
            </div>

            <div className="flex space-x-6 text-sm">
              <button
                onClick={() => navigate('/privacy-policy')}
                className="text-gray-400 hover:text-primary-400 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => {
                  navigate('/privacy-policy');
                  setTimeout(() => {
                    const element = document.getElementById('cookies-section');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="text-gray-400 hover:text-primary-400 transition-colors"
              >
                Cookie Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;